import { z } from 'zod';
import { uuid, paginationQuery } from './common.js';

// ─── Profile update (PATCH /me) ───────────────────────────────────────────
export const updateProfileRequest = z.object({
  full_name: z.string().min(1).max(120).trim().optional(),
  phone: z.string().max(40).trim().nullable().optional(),
  avatar_url: z.string().url().nullable().optional(),
  opt_in_directory: z.boolean().optional(),
  show_phone_in_directory: z.boolean().optional(),
  show_vehicle_in_directory: z.boolean().optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequest>;

// ─── Household member (non-account, e.g. kids, helpers) ───────────────────
export const publicHouseholdMember = z.object({
  id: uuid,
  full_name: z.string(),
  relation: z.string(),
  age: z.number().int().nullable(),
  notes: z.string().nullable(),
});
export type PublicHouseholdMember = z.infer<typeof publicHouseholdMember>;

export const createHouseholdMemberRequest = z.object({
  full_name: z.string().min(1).max(120).trim(),
  relation: z.string().min(1).max(40).trim(),
  age: z.number().int().min(0).max(120).optional(),
  notes: z.string().max(500).optional(),
});
export type CreateHouseholdMemberRequest = z.infer<typeof createHouseholdMemberRequest>;

export const updateHouseholdMemberRequest = createHouseholdMemberRequest.partial();
export type UpdateHouseholdMemberRequest = z.infer<typeof updateHouseholdMemberRequest>;

// ─── Vehicle ───────────────────────────────────────────────────────────────
export const VehicleType = { CAR: 'CAR', MOTORCYCLE: 'MOTORCYCLE' } as const;
export type VehicleType = (typeof VehicleType)[keyof typeof VehicleType];

export const publicVehicle = z.object({
  id: uuid,
  plate: z.string(),
  brand: z.string().nullable(),
  color: z.string().nullable(),
  type: z.enum([VehicleType.CAR, VehicleType.MOTORCYCLE]),
});
export type PublicVehicle = z.infer<typeof publicVehicle>;

export const createVehicleRequest = z.object({
  plate: z
    .string()
    .min(2)
    .max(15)
    .trim()
    .transform((s) => s.toUpperCase().replace(/\s+/g, ' ')),
  brand: z.string().max(60).optional(),
  color: z.string().max(40).optional(),
  type: z.enum([VehicleType.CAR, VehicleType.MOTORCYCLE]),
});
export type CreateVehicleRequest = z.infer<typeof createVehicleRequest>;

export const updateVehicleRequest = createVehicleRequest.partial();
export type UpdateVehicleRequest = z.infer<typeof updateVehicleRequest>;

// ─── Household (GET /me/household) ────────────────────────────────────────
export const publicAccountResident = z.object({
  user_id: uuid,
  full_name: z.string(),
  email: z.string().email(),
  phone: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  is_primary: z.boolean(),
  relation_to_primary: z.string().nullable(),
});
export type PublicAccountResident = z.infer<typeof publicAccountResident>;

export const publicHousehold = z.object({
  unit: z.object({
    id: uuid,
    label: z.string(),
    block: z.string(),
    number: z.string(),
    type: z.enum(['HOUSE', 'APARTMENT', 'RUKO']),
  }),
  primary_resident_id: uuid,
  account_members: z.array(publicAccountResident),
  household_members: z.array(publicHouseholdMember),
  vehicles: z.array(publicVehicle),
  is_viewer_primary: z.boolean(),
});
export type PublicHousehold = z.infer<typeof publicHousehold>;

// ─── Directory (GET /directory, /directory/:user_id) ──────────────────────
export const listDirectoryQuery = paginationQuery.extend({
  block: z.string().max(20).optional(),
  q: z.string().max(60).trim().optional(),
});
export type ListDirectoryQuery = z.infer<typeof listDirectoryQuery>;

export const publicDirectoryEntry = z.object({
  user_id: uuid,
  full_name: z.string(),
  avatar_url: z.string().url().nullable(),
  unit: z.object({
    id: uuid,
    label: z.string(),
    block: z.string(),
    number: z.string(),
  }),
  is_primary: z.boolean(),
  // Only included when opt-in flags allow
  phone: z.string().nullable().optional(),
  vehicle_plates: z.array(z.string()).optional(),
});
export type PublicDirectoryEntry = z.infer<typeof publicDirectoryEntry>;
